import Moment from 'react-moment';
import { Chip } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { Avatar, Badge, Flex, TableProps, Tooltip, Typography } from 'antd';
import {
    FileExcelFilled,
    FileExcelOutlined,
    FileImageFilled,
    FileImageOutlined,
    FileOutlined,
    FilePdfFilled,
    FilePdfOutlined,
    FileWordFilled,
    FileWordOutlined,
    LockFilled,
    FolderOutlined,
} from '@ant-design/icons';
import WTypography from 'containers/WTypography';
import { Category, Document } from 'interfaces/documents';
import { useAppDispatch, useAppSelector } from 'utilities/hooks';
import { isCategory, isDocument } from '../../../utilities/constants';
import ItemActionList from './ItemActionList';
import { User } from 'interfaces/users';
import { clearParams } from 'slices/documentsSlice';
import { DocumentTitleDraggable } from './DocumentTitleDraggable';
import { useAccess } from '@sitemax/smx-permission';
import { MenuItemType } from 'antd/es/menu/interface';

const WIDTH_COLUMN_NAME = 250;
const WIDTH_COLUMN_SIZE = 90;
const WIDTH_COLUMN_CREATED_BY = 120;
const WIDTH_COLUMN_CREATED_AT = 130;
const WIDTH_COLUMN_DELETED_AT = 100;
const WIDTH_COLUMN_UPDATED_AT = 130;
const WIDTH_COLUMN_UPDATED_BY = 120;
const WIDTH_COLUMN_ACTION = 80;
const WIDTH_COLUMN_TEAM = 130;
const WIDTH_COLUMN_FOLDER = 100;

export const DocumentTableColumns = ({
    fetchData,
    navigateTo,
    isDocuments,
    isDeleted,
    draggable,
    filterActions,
}: {
    fetchData: () => void;
    navigateTo?: (categoryId?: number | null, documentId?: string) => void;
    isDocuments: boolean;
    isDeleted: boolean;
    draggable: boolean;
    filterActions?: (actions: MenuItemType[]) => MenuItemType[];
}) => {
    const { teams, params } = useAppSelector((state) => state.documents);
    const dispatch = useAppDispatch();
    const canView = useAccess(['files.all.view']);
    const canViewProject = useAccess(['files.project.view']);

    const [columns, setColumns] = useState<TableProps<Document | Category>['columns']>([]);

    const defaultFilterActions = useCallback((actions: MenuItemType[]) => actions, []);
    const effectiveFilterActions = filterActions || defaultFilterActions;

    const getNameDocumentOrCategory = (name: string) => (
        <Tooltip title={name}>
            <WTypography
                sx={{
                    pl: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    // maxWidth: '150px',
                }}
            >
                {name}
            </WTypography>
        </Tooltip>
    );

    const getIcon = (document: Document) => {
        if (document.extension === 'xlsx' || document.extension === 'xls') {
            if (document.has_markup) return <FileExcelFilled />;
            else return <FileExcelOutlined />;
        } else if (document.extension === 'docx' || document.extension === 'doc') {
            if (document.has_markup) return <FileWordFilled />;
            else return <FileWordOutlined />;
        } else if (document.extension === 'pdf') {
            if (document.has_markup) return <FilePdfFilled />;
            else return <FilePdfOutlined />;
        } else if (
            document.extension === 'jpg' ||
            document.extension === 'jpeg' ||
            document.extension === 'png' ||
            document.extension === 'gif' ||
            document.extension === 'bmp' ||
            document.extension === 'tiff' ||
            document.extension === 'ico'
        ) {
            if (document.has_markup) return <FileImageFilled />;
            else return <FileImageOutlined />;
        }

        return <FileOutlined />;
    };

    useEffect(() => {
        const newColumns: TableProps<Document | Category>['columns'] = [
            {
                key: 'name',
                dataIndex: 'name',
                title: 'Name',
                width: WIDTH_COLUMN_NAME,
                render: (_, record) => {
                    if (isCategory(record)) {
                        return (
                            <Flex>
                                {(record as Category).is_locked ? (
                                    <Badge
                                        count={
                                            <LockFilled
                                                style={{
                                                    color: '#797979',
                                                    fontSize: 12,
                                                }}
                                            />
                                        }
                                        offset={[0, 6]}
                                        className="badge-unset"
                                    >
                                        <FolderOutlined />
                                    </Badge>
                                ) : (
                                    <FolderOutlined />
                                )}
                                {draggable ? (
                                    <DocumentTitleDraggable entity={record as Document | Category} />
                                ) : (
                                    getNameDocumentOrCategory((record as Category).name)
                                )}
                            </Flex>
                        );
                    } else {
                        return (
                            <Flex>
                                {getIcon(record as Document)}
                                {draggable ? (
                                    <DocumentTitleDraggable entity={record as Document | Category} />
                                ) : (
                                    getNameDocumentOrCategory((record as Document).name)
                                )}
                                {(record as Document).version > 1 && (
                                    <Chip label={`v${(record as Document).version}`} size="small" sx={{ ml: 1 }} />
                                )}
                            </Flex>
                        );
                    }
                },
            },
        ];

        !isDeleted &&
            newColumns.push({
                key: 'team',
                dataIndex: 'team',
                title: 'Have Access',
                width: WIDTH_COLUMN_TEAM,
                render: (_, row) => {
                    if (!teams) return <></>;

                    let _team = null;
                    if (isCategory(row)) {
                        _team = teams[(row as Category).uuid];
                    } else {
                        _team = teams[row.id];
                    }
                    if (!_team || !_team.members.length) return <></>;

                    return (
                        <Avatar.Group max={{ count: 3 }}>
                            {_team.members.map((_member: User) => {
                                return (
                                    <Tooltip key={`m${_member.id}`} title={_member.full_name} placement="top">
                                        <Avatar alt={_member.full_name} style={{ width: 32, height: 32, fontSize: 16 }}>
                                            {_member.initials}
                                        </Avatar>
                                    </Tooltip>
                                );
                            })}
                        </Avatar.Group>
                    );
                },
            });

        isDocuments &&
            (params?.query?.length || Object.keys(params?.filters || {}).length) &&
            newColumns.push({
                key: 'folder',
                dataIndex: 'folder',
                title: 'Parent Folder',
                width: WIDTH_COLUMN_FOLDER,
                render: (_, row) => {
                    return (
                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                                navigateTo?.((row as Document).category_id || (row as Category).parent_id || null);
                            }}
                            style={{
                                cursor: 'pointer',
                                color: '#1890ff',
                                display: 'inline-block',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                transition: 'background-color 0.3s',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f5f5f5';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                        >
                            {(row as Document).category_name || 'Root'}
                        </div>
                    );
                },
            });

        newColumns.push({
            key: 'size_formatted',
            dataIndex: 'size_formatted',
            title: 'Size',
            width: WIDTH_COLUMN_SIZE,
        });

        !isDeleted &&
            newColumns.push({
                key: 'created_by',
                dataIndex: 'created_by',
                title: 'Created By',
                width: WIDTH_COLUMN_CREATED_BY,
                render: (_, row) => {
                    if (!row.created_by) return <></>;

                    return (
                        <Tooltip key={`u${row.created_by.id}`} title={row.created_by.full_name} placement="top">
                            <Avatar alt={row.created_by.full_name} style={{ width: 32, height: 32, fontSize: 16 }}>
                                {row.created_by.initials}
                            </Avatar>
                        </Tooltip>
                    );
                },
            });

        !isDeleted &&
            newColumns.push({
                key: 'created_at',
                dataIndex: 'created_at',
                title: 'Uploaded At',
                width: WIDTH_COLUMN_CREATED_AT,
                render: (_, row) => (row.created_at ? <Moment date={row.created_at} format="lll" /> : <></>),
            });

        !isDeleted &&
            newColumns.push({
                key: 'last_touched_at',
                dataIndex: 'last_touched_at',
                title: 'Last Modified',
                width: WIDTH_COLUMN_UPDATED_AT,
                render: (_, row) => <Moment date={row.last_touched_at} fromNow />,
            });

        !isDeleted &&
            newColumns.push({
                key: 'updated_by',
                dataIndex: 'updated_by',
                title: 'Modified By',
                width: WIDTH_COLUMN_UPDATED_BY,
                render: (_, row) => {
                    if (!row.updated_by) return <></>;

                    return (
                        <Tooltip key={`u${row.updated_by.id}`} title={row.updated_by.full_name} placement="top">
                            <Avatar alt={row.updated_by.full_name} style={{ width: 32, height: 32, fontSize: 16 }}>
                                {row.updated_by.initials}
                            </Avatar>
                        </Tooltip>
                    );
                },
            });

        isDeleted &&
            newColumns.push({
                key: 'deleted_at',
                dataIndex: 'deleted_at',
                title: 'Deleted',
                width: WIDTH_COLUMN_DELETED_AT,
                render: (_, row) => <Moment date={row.deleted_at} format="lll" />,
            });

        !isDeleted &&
            newColumns.push({
                width: WIDTH_COLUMN_ACTION,
                render: (_, row) => {
                    if (!row.is_project_document && !canView) return <></>;
                    if (row.is_project_document && !canViewProject) return <></>;
                    return (
                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                            }}
                        >
                            <ItemActionList fetchData={fetchData} row={row} filterActions={effectiveFilterActions}/>
                        </div>
                    );
                },
            });

        setColumns(newColumns);
    }, [teams, params]);

    return columns;
};
